import {getTestProps} from '@benchling/js-test-utils';
import {naturalSortByKey} from '@benchling/js-utils';
import {HeatmapRect} from '@visx/heatmap';
import {HeatmapRectProps} from '@visx/heatmap/lib/heatmaps/HeatmapRect';
import {PatternLines} from '@visx/pattern';
import {scaleLinear} from '@visx/scale';
import {ScaleLinear, extent} from 'd3';
import React, {useCallback, useMemo, useState} from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {DataType, DataPointColumnsToDataType, DeprecatedDataFrame} from 'data-frames/DataFrameTypes';
import {ColumnValidationMap} from 'data-frames/util/AccessorUtils';
import assertWellFormedColumn from 'data-frames/util/legacy/assertWellFormedColumn';
import {ColumnTypeValidationFlag} from 'data-frames/util/validateColumnType';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {AnalyticsVisualizationColorPalette} from 'graphql-client/__generated/graphqlGlobalTypes';
import {GRAY_LIGHT, NEAR_BLACK} from 'ui-library/Colors';
import {JSONValue} from 'util/Types';

import {AxisLeft, AxisOrientation, AxisTop} from './internals/Axis';
import BenchlingChart from './internals/BenchlingChart';
import {useBenchlingChartContext} from './internals/BenchlingChartContext';
import ChartTooltip, {TOOLTIP_LINE_HEIGHT, TooltipInfo, useChartTooltip} from './internals/ChartTooltip';
import GradientSeriesLegend, {
  estimateGradientSeriesLegendWidth,
  GradientSeriesLegendProps,
} from './internals/GradientSeriesLegend';
import HeatmapBox from './internals/HeatmapBox';
import HeatmapWrapper, {HeatmapEventInfo} from './internals/HeatmapWrapper';
import fitChartAndLabels from './internals/utils/fitChartAndLabels';
import getDiscreteAxisNumTicks from './internals/utils/getDiscreteAxisNumTicks';
import {GradientColorScale} from './internals/utils/getSeriesColorConfig';
import getVisxAxisConfig, {
  assertVisxAxisConfigScaleType,
  BandVisxAxisConfig,
} from './internals/utils/getVisxAxisConfig';
import {
  DEFAULT_CHART_MARGIN,
  HEATMAP_COLORS,
  LABEL_FONT_SIZE,
  TICK_LABEL_TO_AXIS,
} from './internals/utils/Theme';
import {VisxAxisScale, VisxScaleType} from './internals/VisxUtilityTypes';
import {AXIS_COMPATIBLE_DATA_TYPES, AxisCompatibleDataType, DiscreteAxisConfig, LegendConfig} from './Types';

// ChartTooltip creates a Tooltip over the hovered HeatmapBox and this offsets it to the right center side.
const HEATMAP_TOOLTIP_HORIZONTAL_OFFSET = 6;
// We need to adjust the tooltip's position slightly, so its center aligns with the hovered bin's center
const HEATMAP_TOOLTIP_VERTICAL_OFFSET = 6;

export const DEFAULT_HEATMAP_MARGINS = {
  top: LABEL_FONT_SIZE + TICK_LABEL_TO_AXIS,
  right: DEFAULT_CHART_MARGIN,
  bottom: DEFAULT_CHART_MARGIN,
  left: LABEL_FONT_SIZE + TICK_LABEL_TO_AXIS,
};

export const DEFAULT_HEATMAP_XAXIS_CONFIG: DiscreteAxisConfig = {
  hideLabel: true,
  label: 'Column',
};

export const DEFAULT_HEATMAP_YAXIS_CONFIG: DiscreteAxisConfig = {
  hideLabel: true,
  label: 'Row',
};

/**
 * @deprecated TODO(BNCH-24546): Remove legacy axis types in favor for the new HEATMAP_TYPE_VALIDATION_MAP
 */
export interface HeatmapAxisTypes {
  x: DataType.NUMBER | DataType.STRING;
  y: DataType.NUMBER | DataType.STRING;
  metric: DataType.NUMBER;
  isMasked?: DataType.BOOLEAN;
}

/**
 * @deprecated TODO(BNCH-24546): Remove legacy axis types in favor for the new HEATMAP_TYPE_VALIDATION_MAP
 */
export const HEATMAP_AXIS_TYPES: {
  [key in keyof HeatmapAxisTypes]: Array<HeatmapAxisTypes[key]>;
} = {
  x: [DataType.NUMBER, DataType.STRING],
  y: [DataType.NUMBER, DataType.STRING],
  metric: [DataType.NUMBER],
  isMasked: [DataType.BOOLEAN],
};

type HeatmapColumns = 'x' | 'y' | 'metric';

export const HEATMAP_TYPE_VALIDATION_MAP: ColumnValidationMap<HeatmapColumns> = {
  x: {
    allowedLeafTypes: [LeafType.DECIMAL, LeafType.INTEGER, LeafType.TEXT],
    nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
    listType: ColumnTypeValidationFlag.NOT_ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
  },
  y: {
    allowedLeafTypes: [LeafType.DECIMAL, LeafType.INTEGER, LeafType.TEXT],
    nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
    listType: ColumnTypeValidationFlag.NOT_ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
  },
  metric: {
    allowedLeafTypes: [LeafType.DECIMAL, LeafType.INTEGER],
    nullableType: ColumnTypeValidationFlag.ALLOWED,
    listType: ColumnTypeValidationFlag.NOT_ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
  },
};

export interface HeatmapConfig {
  legend: LegendConfig;
  ascendingOrder?: boolean;
}

/** A data point passed externally into <Heatmap> */
export interface HeatmapPoint {
  x: JSONValue;
  y: JSONValue;
  metric: JSONValue;
  isMasked?: JSONValue;
}

/**
 * ScaleBand type used for axes of the Heatmap
 */
export type HeatmapAxisScaleType = VisxAxisScale<VisxScaleType.BAND>;

/**
 * A data point used internally by <Heatmap>.
 *
 * Properties' types are transformed from the original data point, but the original data point is tracked in
 * 'point'.
 *
 * If 'point' is null, the HeatmapInternalPoint was generated internally by fillHeatmapGrid and is not
 * a real point in the input data.
 */
export type HeatmapInternalPoint<DataPoint extends HeatmapPoint> = HeatmapPoint & {
  x: string | number;
  y: string | number;
  metric: number | null;
  isMasked: boolean;
  // The original data point. Is null for HeatmapInternalPoints generated by fillHeatmapGrid
  point: DataPoint | null;
};

export interface HeatmapColumn<DataPoint extends HeatmapPoint> {
  x: string | number;
  bins: Array<HeatmapInternalPoint<DataPoint>>;
}

/**
 * @deprecated TODO(BNCH-24546): Remove legacy data in favor for the new DataFrame
 */
export type HeatmapData<DataPoint extends HeatmapPoint> = DeprecatedDataFrame<DataPoint>;

export interface HeatmapProps<DataPoint extends HeatmapPoint> {
  config: HeatmapConfig;
  data: HeatmapData<DataPoint>;
  onClickPoint?: (point: DataPoint) => void;
}

/**
 * Translate points from the source data set into the form used internally by <Heatmap>.
 */
export function convertPointsIntoHeatmapInternalPoints<DataPoint extends HeatmapPoint>(
  points: Array<DataPoint>
): Array<HeatmapInternalPoint<DataPoint>> {
  return points.map((point) => ({
    x: point.x as string | number,
    y: point.y as string | number,
    metric: point.metric as number | null,
    isMasked: Boolean(point.isMasked),
    point,
  }));
}

/**
 * Return a HeatmapInternalPoint array after filling missing HeatmapPoints.
 *
 * Visx's HeatmapRect requires all possible combinations of 'x' and 'y' values to be in the HeatmapPoint array
 * to be rendered correctly. This function ensures that for all 'x' and 'y' which appear at least once in the
 * given HeatmapPoint array, there is a HeatmapPoint {'x', 'y', 'metric'} with 'metric' set to the given value
 * or 'null' if missing.
 */
export function fillHeatmapGrid<DataPoint extends HeatmapPoint>(
  points: Array<HeatmapInternalPoint<DataPoint>>
): Array<HeatmapInternalPoint<DataPoint>> {
  const xKeyToColArrays = _.groupBy(points, 'x');
  // eslint-disable-next-line no-restricted-syntax
  const xKeys = _.uniq(points.map((point) => point.x));
  // eslint-disable-next-line no-restricted-syntax
  const yKeys = _.uniq(points.map((point) => point.y));

  const xKeyToAbsentYCols = xKeys.map((x) => ({
    x,
    absentYArray: yKeys.filter((y) => !xKeyToColArrays[x].some((point) => point.y === y)),
  }));
  const missingPoints = xKeyToAbsentYCols.flatMap(({x, absentYArray}) =>
    absentYArray.map((y) => ({x, y, metric: null, isMasked: false, point: null}))
  );

  return [...points, ...missingPoints];
}

/**
 * Return a prepared HeatmapInternalPoint array.
 *
 * The function filters duplicates (those with the same 'x' and 'y' values) in the HeatmapPoint array by only
 * keeping the last occurrence of the point in the array. Then, it calls fillHeatmapGrid to fill HeatmapPoints
 * as necessary and returns the result after sorting points by 'x' values, and for the same 'x' values,
 * points are sorted by their 'y' value.
 */
export function prepareHeatmapPoints<DataPoint extends HeatmapPoint>(
  points: Array<DataPoint>,
  axisTypes: DataPointColumnsToDataType<DataPoint>
): Array<HeatmapInternalPoint<DataPoint>> {
  const filteredPoints = convertPointsIntoHeatmapInternalPoints(points)
    .slice()
    .reverse()
    .filter(
      (point, index, points) =>
        points.findIndex((currentPoint) => currentPoint.x === point.x && currentPoint.y === point.y) === index
    );

  const numericSortByKey = <DataPoint extends HeatmapPoint>(
    points: Array<HeatmapInternalPoint<DataPoint>>,
    key: keyof HeatmapPoint
  ): Array<HeatmapInternalPoint<DataPoint>> => _.sortBy(points, key);
  const xSortFunction = axisTypes.x === DataType.NUMBER ? numericSortByKey : naturalSortByKey;
  const ySortFunction = axisTypes.y === DataType.NUMBER ? numericSortByKey : naturalSortByKey;

  return xSortFunction(ySortFunction(fillHeatmapGrid(filteredPoints), 'y'), 'x');
}

export function getColumnData<DataPoint extends HeatmapPoint>(
  points: Array<HeatmapInternalPoint<DataPoint>>
): Array<HeatmapColumn<DataPoint>> {
  const xKeyToFilledColArrays = _.groupBy(points, 'x');
  const xValues = [...new Set(points.map((point) => point.x))];

  return xValues.map((x) => ({x, bins: xKeyToFilledColArrays[x]}));
}

export function getColorScale<DataPoint extends HeatmapPoint>(
  config: HeatmapConfig,
  points: Array<HeatmapInternalPoint<DataPoint>>
): GradientColorScale {
  const colorScale = scaleLinear({
    range: HEATMAP_COLORS,
  });
  const colorDomain = extent(
    points.filter((point) => !point.isMasked).map((point) => point.metric as number)
  ) as [number, number];
  colorScale.domain(config.ascendingOrder ? colorDomain : colorDomain.slice().reverse());

  return colorScale;
}

export function getHeatmapLegendProps(
  colorScale: GradientColorScale,
  config: LegendConfig,
  heatmapHeight: number,
  width: number
): GradientSeriesLegendProps {
  const [minMetricValue, maxMetricValue] = colorScale.domain();
  const heatmapWidth =
    width - estimateGradientSeriesLegendWidth(config.label, minMetricValue, maxMetricValue);

  return {
    colorConfig: {colorPalette: AnalyticsVisualizationColorPalette.GRADIENT, colorScale},
    chartHeight: heatmapHeight,
    chartWidth: heatmapWidth,
    label: config.label,
  };
}

/**
 * Create `VisxAxisConfig`'s used to generate tick labels on the X and Y axes on the chart.
 */
export function getHeatmapAxisConfigs<DataPoint extends HeatmapPoint>(
  chartHeight: number,
  chartWidth: number,
  points: Array<HeatmapInternalPoint<DataPoint>>,
  axisTypes: DataPointColumnsToDataType<DataPoint>
): {xAxisConfig: BandVisxAxisConfig; yAxisConfig: BandVisxAxisConfig} {
  const xAxisType = axisTypes.x as AxisCompatibleDataType;
  const yAxisType = axisTypes.y as AxisCompatibleDataType;

  assert(AXIS_COMPATIBLE_DATA_TYPES.includes(xAxisType), 'Heatmap has invalid x-axis');
  assert(AXIS_COMPATIBLE_DATA_TYPES.includes(yAxisType), 'Heatmap has invalid y-axis');

  const xValues = [...new Set(points.map((point) => point.x))];
  const xNumTicks = getDiscreteAxisNumTicks(xValues.length, chartWidth);

  const xAxisConfig = getVisxAxisConfig(DEFAULT_HEATMAP_XAXIS_CONFIG, xAxisType, xValues, {
    sortDomain: true,
    suggestedNumTicks: xNumTicks,
  });

  const yValues = [...new Set(points.map((point) => point.y))];
  const yNumTicks = getDiscreteAxisNumTicks(yValues.length, chartHeight);
  const yAxisConfig = getVisxAxisConfig(DEFAULT_HEATMAP_YAXIS_CONFIG, yAxisType, yValues, {
    sortDomain: true,
    suggestedNumTicks: yNumTicks,
  });

  assertVisxAxisConfigScaleType(xAxisConfig, VisxScaleType.BAND);
  assertVisxAxisConfigScaleType(yAxisConfig, VisxScaleType.BAND);

  return {xAxisConfig, yAxisConfig};
}

/**
 * Create scales used internally by HeatmapRect to position the HeatmapBox components.
 */
export function getHeatmapRectXYScales<DataPoint extends HeatmapPoint>(
  chartHeight: number,
  chartWidth: number,
  points: Array<Pick<DataPoint, 'x' | 'y'>>
): {
  xScale: ScaleLinear<number, number>;
  yScale: ScaleLinear<number, number>;
} {
  // eslint-disable-next-line no-restricted-syntax
  const numColumns = _.uniq(points, 'x').length;
  // eslint-disable-next-line no-restricted-syntax
  const numRows = _.uniq(points, 'y').length;

  const xScale = scaleLinear({
    domain: [0, numColumns],
    range: [0, chartWidth],
  });
  const yScale = scaleLinear({
    domain: [0, numRows],
    range: [0, chartHeight],
  });

  return {xScale, yScale};
}

export function getHeatmapRectProps<DataPoint extends HeatmapPoint>(
  chartHeight: number,
  chartWidth: number,
  colorScale: ScaleLinear<string, string>,
  points: Array<HeatmapInternalPoint<DataPoint>>
): HeatmapRectProps<HeatmapColumn<DataPoint>, HeatmapInternalPoint<DataPoint>> {
  const columnData = getColumnData(points);
  // eslint-disable-next-line no-restricted-syntax
  const numRows = _.uniq(points, 'y').length;
  const scales = getHeatmapRectXYScales(chartHeight, chartWidth, points);

  return {
    ...scales,
    binHeight: chartHeight / numRows,
    binWidth: chartWidth / columnData.length,
    colorScale,
    data: columnData,
    gap: 0,
  };
}

export function getTooltipInfo<DataPoint extends HeatmapPoint>(
  eventInfo: HeatmapEventInfo<DataPoint>,
  heatmapMargins: {top: number; left: number}
): TooltipInfo<HeatmapInternalPoint<DataPoint>> {
  return {
    // Calculate the exact x position of the right side of the bin
    left: eventInfo.x + eventInfo.targetBinWidth + heatmapMargins.left,
    // Add some padding between the tooltip and the bin
    offsetLeft: HEATMAP_TOOLTIP_HORIZONTAL_OFFSET,
    // Calculate the exact center point of the bin
    top: eventInfo.y + eventInfo.targetBinHeight / 2 + heatmapMargins.top,
    // Add some vertical padding upwards, so the center of the tooltip (not the top of the tooltip) aligns
    // with the center of the bin. This should be half the total height of the tooltip box.
    // (visx simply adds top and offsetTop together, but defaults offsetTop to 10px if we don't specify)
    offsetTop: -(TOOLTIP_LINE_HEIGHT * 1.5 + HEATMAP_TOOLTIP_VERTICAL_OFFSET),
    point: eventInfo.dataPoint,
  };
}

export function getOnClickPointFromEventInfo<DataPoint extends HeatmapPoint>(
  eventInfo: HeatmapEventInfo<DataPoint> | null
): DataPoint | null {
  return eventInfo?.dataPoint?.point ?? null;
}

function _HeatmapGridArea<DataPoint extends HeatmapPoint>({
  chartHeight,
  chartWidth,
  heatmapRectProps,
}: {
  chartHeight: number;
  chartWidth: number;
  heatmapRectProps: HeatmapRectProps<HeatmapColumn<DataPoint>, HeatmapInternalPoint<DataPoint>>;
  onClickPoint?: (point: DataPoint) => void;
}): JSX.Element {
  return (
    <>
      <HeatmapRect<HeatmapColumn<DataPoint>, HeatmapInternalPoint<DataPoint>>
        {...heatmapRectProps}
        count={(point) => point.metric as number}
      >
        {(heatmap) =>
          heatmap.map((heatmapBins) =>
            heatmapBins.map((bin) =>
              bin.bin.point?.metric == null ? null : (
                <HeatmapBox
                  {...bin}
                  color={bin.color}
                  heatmapInnerBin={bin.bin}
                  key={`heatmap-rect-${bin.row}-${bin.column}`}
                />
              )
            )
          )
        }
      </HeatmapRect>
      <rect
        fill="transparent"
        height={chartHeight}
        pointerEvents="none"
        stroke={GRAY_LIGHT}
        width={chartWidth}
      />
    </>
  );
}

const HeatmapGridArea = React.memo(_HeatmapGridArea);

/**
 * @deprecated Charts should be created through backend Chart model and rendered through /charts/chart-content module. Reach out
 * to Insights team for more details on charting.
 */
export function Heatmap<DataPoint extends HeatmapPoint>({
  config,
  data: {axisTypes, points},
  onClickPoint,
  height,
  width,
}: HeatmapProps<DataPoint> & {height: number; width: number}): JSX.Element {
  assertWellFormedColumn({rows: points, columnTypes: axisTypes}, 'isMasked');

  const {
    chartHeight,
    chartWidth,
    heatmapLegendProps,
    heatmapMargins,
    heatmapRectProps,
    preparedPoints,
    xAxisConfig,
    yAxisConfig,
  } = useMemo(() => {
    const preparedPoints = prepareHeatmapPoints(points, axisTypes);
    const newConfig = {ascendingOrder: true, ...config};
    const colorScale = getColorScale(newConfig, preparedPoints);
    const heatmapLegendProps = getHeatmapLegendProps(colorScale, newConfig.legend, height, width);

    const defaultMargins = {
      ...DEFAULT_HEATMAP_MARGINS,
      right: width - heatmapLegendProps.chartWidth,
    };

    const initialAxisConfigs = getHeatmapAxisConfigs(
      height,
      heatmapLegendProps.chartWidth,
      preparedPoints,
      axisTypes
    );
    const {
      chartWidth,
      chartHeight,
      margins: heatmapMargins,
      xAxisConfig,
      yAxisConfig,
    } = fitChartAndLabels({
      defaultMargins,
      height,
      width,
      xAxisOrientation: AxisOrientation.TOP,
      ...initialAxisConfigs,
    });

    const heatmapRectProps = getHeatmapRectProps(chartHeight, chartWidth, colorScale, preparedPoints);

    return {
      chartHeight,
      chartWidth,
      heatmapLegendProps,
      heatmapMargins,
      heatmapRectProps,
      preparedPoints,
      xAxisConfig,
      yAxisConfig,
    };
  }, [config, axisTypes, height, width, points]);

  const {tooltipProps, updateTooltipProps} = useChartTooltip<Pick<HeatmapPoint, 'x' | 'y' | 'metric'>>({
    axisTypes,
    columnNames: ['y', 'x', 'metric'],
    columnLabels: {
      x: DEFAULT_HEATMAP_XAXIS_CONFIG.label,
      y: DEFAULT_HEATMAP_YAXIS_CONFIG.label,
      metric: config.legend.label,
    },
  });
  const [hoveredBox, setHoveredBox] = useState<HeatmapEventInfo<DataPoint> | null>(null);

  const handleOnHover = useCallback(
    (positionInfo: HeatmapEventInfo<DataPoint> | null) => {
      setHoveredBox(positionInfo);
      updateTooltipProps(positionInfo == null ? null : getTooltipInfo(positionInfo, heatmapMargins));
    },
    [updateTooltipProps, heatmapMargins]
  );

  const handleOnClick =
    onClickPoint == null
      ? undefined
      : (eventInfo: HeatmapEventInfo<DataPoint>) => {
          const clickablePoint = getOnClickPointFromEventInfo(eventInfo);
          if (clickablePoint != null) {
            onClickPoint(clickablePoint);
          }
        };

  const {clientId} = useBenchlingChartContext();

  const backgroundPatternId = `lines-${clientId}`;

  return (
    <svg height={height} width={width} shapeRendering="crispEdges">
      <HeatmapWrapper
        offsetPosition={heatmapMargins}
        points={preparedPoints}
        xScale={xAxisConfig.scale}
        yScale={yAxisConfig.scale}
        onClickPoint={handleOnClick}
        onMouseLeave={handleOnHover}
        onMouseMove={handleOnHover}
      >
        <PatternLines
          id={backgroundPatternId}
          height={20}
          width={20}
          stroke={GRAY_LIGHT}
          strokeWidth={1}
          orientation={['diagonal']}
        />
        <rect fill={`url(#${backgroundPatternId})`} height={chartHeight} width={chartWidth} />
        <HeatmapGridArea
          chartHeight={chartHeight}
          chartWidth={chartWidth}
          heatmapRectProps={heatmapRectProps}
        />
        <AxisLeft axisConfig={yAxisConfig} hideAxisLine={true} />
        <AxisTop axisConfig={xAxisConfig} hideAxisLine={true} />
        {tooltipProps && <ChartTooltip {...tooltipProps} />}
        {/* The Heatmap component renders all HeatmapBox in order starting from the top left box and changing
        the stroke width of the actual hovered HeatmapBox metric values in the border being overlapped by
        boxes on its right and bottom. Here, a transparent rectangle is created to highlight the border of the
        currently hovered HeatmapBox and since it is rendered after all the boxes, this solves the issue. */}
        {hoveredBox == null ? null : (
          <rect
            fill="transparent"
            height={hoveredBox.targetBinHeight}
            pointerEvents="none"
            stroke={NEAR_BLACK}
            strokeWidth={3}
            width={hoveredBox.targetBinWidth}
            x={hoveredBox.x}
            y={hoveredBox.y}
            {...getTestProps({component: 'Heatmap', element: 'hoveredRect'})}
          />
        )}
      </HeatmapWrapper>
      <GradientSeriesLegend {...heatmapLegendProps} />
    </svg>
  );
}

/**
 * @deprecated Charts should be created through backend Chart model and rendered through /charts/chart-content module. Reach out
 * to Insights team for more details on charting.
 */
export default function HeatmapChartWrapper<DataPoint extends HeatmapPoint>(
  props: HeatmapProps<DataPoint>
): JSX.Element {
  return (
    <BenchlingChart>
      {({height, width}) => <Heatmap {...props} height={height} width={width} />}
    </BenchlingChart>
  );
}
